
import React, { useState, useEffect, useRef } from 'react';
// Corrected import path for types.ts
import { User, Article, ArticleStatus, Sector, Attachment, UserRole } from '@/types'; // Corrected import path
import { Send, FileUp, AlertCircle, CheckCircle2, Save, X, Paperclip, Loader2, ShieldCheck, AlertTriangle, Info, RefreshCw, FileSpreadsheet, FileText, Image, FolderOpen, Plus } from 'lucide-react';

interface ArticleSubmissionProps {
  currentUser: User;
  sectors: Sector[];
  onSubmit: (article: Article, files: File[]) => Promise<void>;
  onSaveSector: (sector: Sector) => Promise<void>; // REINTRODUZIDO: A criação de setor agora é inline para sub-pastas
  initialData?: Article | null; // ADDED: initialData prop for editing existing articles
}

export const ArticleSubmission: React.FC<ArticleSubmissionProps> = ({ currentUser, sectors, onSubmit, onSaveSector, initialData }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedMainSectorId, setSelectedMainSectorId] = useState<string | null>(null);
  const [selectedSubSectorId, setSelectedSubSectorId] = useState<string | null>(null);
  const [tags, setTags] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // NOVOS ESTADOS para criação dinâmica de SUB-SETOR
  const [isCreatingNewSubSector, setIsCreatingNewSubSector] = useState(false);
  const [newSubSectorName, setNewSubSectorName] = useState('');

  // Fix: Initialize useRef with null.
  const lastLoadedIdRef = useRef<string | null>(null);

  const isAdmin = currentUser.role === UserRole.ADMIN;
  const canPublish = currentUser.role === UserRole.PUBLISHER || isAdmin;

  useEffect(() => {
    const targetId = initialData ? initialData.id : 'new_article_mode';
    if (lastLoadedIdRef.current === targetId) return;
    lastLoadedIdRef.current = targetId;

    if (initialData) {
      setTitle(initialData.title);
      setContent(initialData.content);
      setTags(initialData.tags.join(', '));
      setExistingAttachments(initialData.attachments || []);

      // Lógica para preencher Setor Principal e Sub-Pasta na edição
      const currentSector = sectors.find(s => s.id === initialData.sectorId);
      if (currentSector) {
        if (currentSector.parentId) {
          // É uma sub-pasta
          setSelectedMainSectorId(currentSector.parentId);
          setSelectedSubSectorId(currentSector.id);
        } else {
          // É um setor principal
          setSelectedMainSectorId(currentSector.id);
          setSelectedSubSectorId(null);
        }
      } else {
        // Fallback se o setor não for encontrado
        setSelectedMainSectorId(null);
        setSelectedSubSectorId(null);
      }

      setSubmitted(false);
      setSubmissionError(null);
      // Reset de estados de criação de sub-setor
      setIsCreatingNewSubSector(false);
      setNewSubSectorName('');
    } else {
      const draftStr = localStorage.getItem('wiki_new_article_draft');
      if (draftStr) {
        try {
          const draft = JSON.parse(draftStr);
          setTitle(draft.title || '');
          setContent(draft.content || '');
          setSelectedMainSectorId(draft.selectedMainSectorId || null);
          setSelectedSubSectorId(draft.selectedSubSectorId || null);
          setTags(draft.tags || '');
        } catch (e) {
          console.error("Error parsing article draft:", e);
          setTitle('');
          setContent('');
          setSelectedMainSectorId(null);
          setSelectedSubSectorId(null);
          setTags('');
        }
      } else {
        setTitle('');
        setContent('');
        setSelectedMainSectorId(null);
        setSelectedSubSectorId(null);
        setTags('');
      }
      setFiles([]);
      setExistingAttachments([]);
      setSubmitted(false);
      setSubmissionError(null);
      // Reset de estados de criação de sub-setor
      setIsCreatingNewSubSector(false);
      setNewSubSectorName('');
    }
  }, [initialData, sectors]);

  // Salva o rascunho em localStorage quando os campos do formulário mudam (apenas no modo de novo artigo)
  useEffect(() => {
    if (!initialData) {
      const draft = {
        title,
        content,
        selectedMainSectorId,
        selectedSubSectorId,
        tags
      };
      localStorage.setItem('wiki_new_article_draft', JSON.stringify(draft));
    }
  }, [title, content, selectedMainSectorId, selectedSubSectorId, tags, initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmissionError(null);

    let finalSectorId: string | null = null;
    let finalSectorName: string = '';

    try {
      // Validação do Setor Principal
      if (!selectedMainSectorId) {
        throw new Error("Selecione um Setor Principal para o artigo.");
      }

      // Lógica de criação ou seleção de Sub-Pasta
      if (isCreatingNewSubSector) {
        const trimmedNewSubSectorName = newSubSectorName.trim();
        if (!trimmedNewSubSectorName) {
          throw new Error("O nome da nova sub-pasta não pode ser vazio.");
        }

        // Validação para nome duplicado de sub-setor dentro do setor principal
        const isDuplicateSubSector = sectors.some(s =>
          s.parentId === selectedMainSectorId && // O pai é o setor principal selecionado
          s.name.toLowerCase() === trimmedNewSubSectorName.toLowerCase()
        );

        if (isDuplicateSubSector) {
          throw new Error("Já existe uma sub-pasta com este nome neste setor. Escolha um nome diferente.");
        }

        const newSubSector: Sector = {
          id: Date.now().toString(), // Um ID simples para o novo setor
          name: trimmedNewSubSectorName,
          parentId: selectedMainSectorId, // O pai é o setor principal selecionado
        };
        await onSaveSector(newSubSector); // Salva a nova sub-pasta
        finalSectorId = newSubSector.id;
        finalSectorName = newSubSector.name;
      } else if (selectedSubSectorId) {
        // Sub-pasta existente selecionada
        finalSectorId = selectedSubSectorId;
        finalSectorName = sectors.find(s => s.id === selectedSubSectorId)?.name || 'Desconhecido';
      } else {
        // Apenas setor principal selecionado, sem sub-pasta
        finalSectorId = selectedMainSectorId;
        finalSectorName = sectors.find(s => s.id === selectedMainSectorId)?.name || 'Desconhecido';
      }

      if (!finalSectorId) {
        throw new Error("Selecione uma Pasta ou Setor para o artigo.");
      }

      const determineStatus = () => {
        if (currentUser.role === UserRole.ADMIN) return ArticleStatus.APPROVED;
        return ArticleStatus.PENDING;
      };

      const article: Article = {
        id: initialData ? initialData.id : Date.now().toString(),
        title,
        content,
        authorId: initialData ? initialData.authorId : currentUser.id,
        authorName: initialData ? initialData.authorName : currentUser.fullName,
        sectorId: finalSectorId,
        sectorName: finalSectorName,
        status: determineStatus(),
        createdAt: initialData ? initialData.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        tags: tags.split(',').map(t => t.trim()).filter(t => t),
        attachments: existingAttachments
      };

      await onSubmit(article, files);
      localStorage.removeItem('wiki_new_article_draft');
      setSubmitted(true);
      if (!initialData) {
        setTimeout(() => {
          setTitle(''); setContent(''); setTags(''); setFiles([]); setExistingAttachments([]);
          setSelectedMainSectorId(null); setSelectedSubSectorId(null); // Resetar seleções de setor
          setIsCreatingNewSubSector(false); setNewSubSectorName(''); // Resetar estados da nova sub-pasta
          setSubmitted(false);
        }, 3000);
      }
    } catch (error: any) {
      setSubmissionError(error.message || "Ocorreu um erro inesperado ao salvar.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles: File[] = Array.from(e.target.files);
      const validFiles: File[] = [];
      newFiles.forEach((file: File) => {
        // Check for supported file types (PDF, Images, Word, Excel)
        const isPdf = file.type.includes('pdf');
        const isImage = file.type.includes('image');
        const isWord = file.type.includes('word') || file.type.includes('document') || file.name.toLowerCase().endsWith('.docx') || file.name.toLowerCase().endsWith('.doc');
        const isExcel = file.type.includes('spreadsheet') || file.type.includes('excel') || file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');

        if (!isPdf && !isImage && !isWord && !isExcel) {
          alert(`O arquivo "${file.name}" não é suportado. Por favor, anexe apenas PDFs, imagens ou arquivos Office (Word/Excel).`);
          return; // Skip this file
        }
        // Limite de tamanho
        if (file.size > 1024 * 1024 * 50) {
          alert(`O arquivo "${file.name}" excede 50MB. Por favor, otimize o arquivo.`);
        } else {
          validFiles.push(file);
        }
      });
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFile = (index: number) => {
    if (window.confirm("Deseja remover este arquivo da lista de envio?")) {
      setFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const removeExistingAttachment = (attId: string) => {
    if (window.confirm("Deseja remover este anexo permanentemente? O arquivo será desvinculado ao salvar.")) {
      setExistingAttachments(prev => prev.filter(a => a.id !== attId));
    }
  };

  const topLevelSectors = sectors.filter(s => !s.parentId);
  const subSectors = sectors.filter(s => s.parentId === selectedMainSectorId);

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 max-w-4xl mx-auto animate-fade-in">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-black text-slate-800 tracking-tight uppercase">
            {initialData ? 'Editar Conhecimento' : 'Publicar'}
          </h2>
          <p className="text-slate-500 font-medium">Submeta textos, imagens ou documentos PDF/Office.</p>
        </div>
        <div className={`px-4 py-2 rounded-2xl flex items-center gap-2 border text-xs font-black uppercase tracking-wider ${isAdmin ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-orange-50 text-orange-700 border-orange-100'}`}>
          {isAdmin ? <ShieldCheck className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {isAdmin ? 'Publicação Direta' : 'Requer Moderação'}
        </div>
      </div>

      {submissionError && (
        <div className="mb-6 p-6 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-4 animate-in slide-in-from-top-4">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div>
            <h4 className="font-black text-red-800 uppercase text-sm mb-1">Erro ao Salvar</h4>
            <p className="text-red-700 text-sm leading-relaxed">{submissionError}</p>
          </div>
        </div>
      )}

      {submitted ? (
        <div className="bg-green-50 border border-green-200 rounded-3xl p-12 text-center animate-fade-in">
          <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <h3 className="text-2xl font-black text-green-800 uppercase mb-2">Sucesso Total!</h3>
          <p className="text-green-700 font-medium">{isAdmin ? 'Disponível imediatamente na rede.' : 'Enviado para aprovação dos administradores.'}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">Título do Conhecimento</label>
              <input required value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-slate-800" placeholder="Ex: Manual de Operação do CRM" />
            </div>

            {/* Seleção de Setor Principal e Sub-Pasta/Categoria */}
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">Setor Principal</label>
              <select
                required
                value={selectedMainSectorId || ''}
                onChange={(e) => {
                  setSelectedMainSectorId(e.target.value || null);
                  setSelectedSubSectorId(null); // Reset sub-sector when main sector changes
                  setIsCreatingNewSubSector(false); // Reset new sub-sector creation state
                  setNewSubSectorName(''); // Clear new sub-sector name
                }}
                className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-slate-800 select-arrow"
              >
                <option value="">Selecione um Setor Principal</option>
                {topLevelSectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>

              {selectedMainSectorId && (
                <div className="mt-4">
                  <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">Sub-Pasta / Categoria (Opcional)</label>
                  <select
                    value={isCreatingNewSubSector ? 'CREATE_NEW' : (selectedSubSectorId || '')}
                    onChange={(e) => {
                      if (e.target.value === 'CREATE_NEW') {
                        setIsCreatingNewSubSector(true);
                        setSelectedSubSectorId(null); // Nenhuma sub-pasta existente selecionada
                        setNewSubSectorName(''); // Limpa o input de novo nome
                        setSubmissionError(null); // Clear potential error when starting new creation
                      } else {
                        setIsCreatingNewSubSector(false); 
                        setSelectedSubSectorId(e.target.value || null);
                        setNewSubSectorName(''); // Limpa o input de novo nome
                        setSubmissionError(null); // Clear potential error when selecting existing
                      }
                    }}
                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-slate-800 select-arrow"
                    disabled={!canPublish && subSectors.length === 0} // Disable if no sub-sectors and user can't create
                  >
                    <option value="">Nenhuma Sub-Pasta</option>
                    {subSectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    {canPublish && <option value="CREATE_NEW">--- Criar Nova Sub-Pasta ---</option>}
                  </select>

                  {isCreatingNewSubSector && (
                    <div className="mt-4">
                      <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">Nome da Nova Sub-Pasta</label>
                      <input
                        type="text"
                        value={newSubSectorName}
                        onChange={(e) => {
                          setNewSubSectorName(e.target.value);
                          setSubmissionError(null); // Clear error on change
                        }}
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-slate-800"
                        placeholder="Ex: Marketing Digital Interno"
                        required
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">Conteúdo do Procedimento</label>
            <textarea required value={content} onChange={(e) => setContent(e.target.value)} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 min-h-[300px]" placeholder="Descreva o passo a passo..." />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest">Tags Relacionadas</label>
              <input value={tags} onChange={(e) => setTags(e.target.value)} className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Financeiro, RH, Tutorial..." />
            </div>

            <div>
              <label className="block text-xs font-black text-slate-500 uppercase mb-2 tracking-widest flex justify-between items-center">
                <span>Anexos</span>
                <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded uppercase font-black">Máx 50MB</span>
              </label>
              <div className="relative border-2 border-dashed border-slate-200 rounded-2xl p-6 hover:bg-slate-50 transition-all text-center cursor-pointer group">
                <input type="file" multiple onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" accept=".pdf,.png,.jpg,.jpeg,.docx,.doc,.xlsx,.xls" />
                <div className="flex flex-col items-center">
                  <div className="flex gap-2 mb-2">
                    <FileText className="w-5 h-5 text-red-400" /> {/* PDF icon */}
                    <Image className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-sm font-bold text-slate-500">Clique para anexar arquivos</span>
                  <span className="text-[10px] text-slate-400 mt-1 uppercase tracking-tighter font-black">PDF, Imagens, Word ou Excel até 50MB</span>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {[...existingAttachments, ...files.map((f, i) => ({ id: `new-${i}`, name: f.name, type: f.type, url: '' }))].map((att, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-100 text-xs font-bold text-slate-700">
                    <div className="flex items-center gap-2 truncate">
                      <Paperclip className="w-3 h-3 text-blue-500" />
                      <span className="truncate max-w-[200px]">{att.name}</span>
                    </div>
                    <button type="button" onClick={() => att.id.startsWith('new-') ? removeFile(parseInt(att.id.split('-')[1])) : removeExistingAttachment(att.id)} className="text-slate-400 hover:text-red-500 p-1">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-6 border-t border-slate-100">
            <button type="submit" disabled={isSubmitting} className="flex items-center gap-2 bg-slate-900 text-white px-10 py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-slate-800 shadow-xl disabled:opacity-50">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={16} />}
              {initialData ? 'Atualizar Conhecimento' : (isAdmin ? 'Publicar Agora' : 'Submeter p/ Revisão')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};